import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import jwt_decode from "jwt-decode";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { captureException } from "@sentry/react";
import { useGetTeammate } from "@/features/teammates/hooks/use-get-teammate";
import { pushToDataLayer } from "@/data-layer/push-to-data-layer";
import { useOnQueryError } from "@/hooks";
import { HydraErrorResponse } from "@/types";
import StorageService from "@/services";
import queryClient from "@/lib/query-client";
import { Account } from "@/features/accounts";
import { AccessLevel, Permission } from "@/features/teammates/types";
import { getPrimaryAccessLevel } from "@/features/teammates/utils";
import { checkUserPermissions } from "@/features/auth/utils/check-user-permissions";
import { useGetAccountDetails, useLogout } from "../hooks";
import { AuthenticatedUser, Credentials, LoginSuccessResponse, LogoutParams, TokenKey } from "../types";
import { AuthService } from "../services";

const AuthContext = createContext<{
  accountDetails: Account | undefined;
  login: UseMutateFunction<LoginSuccessResponse, AxiosError<HydraErrorResponse>, Credentials>;
  logout: (params?: LogoutParams) => void;
  user: AuthenticatedUser | null;
  isLoginPending: boolean;
  isUserDetailsLoading: boolean;
  isLogoutPending: boolean;
  reloadUser: () => void;
  userGlobalAccessLevel: AccessLevel | undefined;
  isUserPermitted: (requiredPermissions: Permission[], solutionId?: number | string) => boolean;
} | null>(null);

const decodeUserToken = (token: string | null): AuthenticatedUser | null => {
  if (!token) return null;

  try {
    return jwt_decode<AuthenticatedUser>(token);
  } catch (error) {
    captureException(error);

    return null;
  }
};

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("coi-platform-translations", {
    keyPrefix: "auth",
  });
  const [user, setUser] = useState<AuthenticatedUser | null>(() =>
    decodeUserToken(StorageService.getItem(TokenKey.TOKEN)),
  );

  const onError = useOnQueryError("error.backend.default_error");
  const { mutateAsync: appLogout, isPending: isLogoutPending } = useLogout();
  const { enqueueSnackbar } = useSnackbar();
  const { data: accountDetails } = useGetAccountDetails(user?.account_id || null);

  const reloadUser = useCallback(() => {
    const token = StorageService.getItem(TokenKey.TOKEN);
    setUser(decodeUserToken(token));
  }, []);

  const removeUser = useCallback(() => {
    StorageService.removeItem(TokenKey.TOKEN);
    reloadUser();
  }, [reloadUser]);

  const { isPending: isLoginPending, mutate } = useMutation<
    LoginSuccessResponse,
    AxiosError<HydraErrorResponse>,
    Credentials
  >({
    mutationKey: ["login"],
    mutationFn: AuthService.login,
    onSuccess: () => {
      reloadUser();
    },
    onError,
  });

  const { data: userDetails, isLoading: isUserDetailsLoading } = useGetTeammate(user?.current_user_id);

  const setLogoutNotification = useCallback(
    (unauthorized = true) => {
      enqueueSnackbar(t(unauthorized ? "notification_unauthorized_error" : "notification_logout_message"), {
        variant: unauthorized ? "error" : "success",
      });
    },
    [enqueueSnackbar, t],
  );

  const logout = useCallback(
    async ({ unauthorized = false, skipBackend = false, skipNotification = false }: LogoutParams = {}) => {
      try {
        if (!skipBackend) {
          await appLogout();
        }
      } catch (error) {
        captureException(error);
      } finally {
        await queryClient.cancelQueries();
        removeUser();

        if (!skipNotification) {
          setLogoutNotification(unauthorized);
        }

        queryClient.removeQueries();
      }
    },
    [appLogout, removeUser, setLogoutNotification],
  );

  const isUserPermitted = useCallback(
    (requiredPermissions: Permission[], consentSolutionId?: number | string) => {
      return checkUserPermissions(userDetails, requiredPermissions, consentSolutionId);
    },
    [userDetails],
  );

  useEffect(() => {
    if (user?.account_id) {
      pushToDataLayer("accountIdSet", { accountId: user.account_id });
    }
  }, [user?.account_id]);

  const userGlobalAccessLevel = getPrimaryAccessLevel(userDetails?.accessLevel);

  const contextValue = useMemo(
    () => ({
      accountDetails,
      user,
      userGlobalAccessLevel,
      login: mutate,
      isLoginPending,
      isUserDetailsLoading,
      logout,
      isLogoutPending,
      reloadUser,
      isUserPermitted,
    }),
    [
      accountDetails,
      userGlobalAccessLevel,
      user,
      mutate,
      isLoginPending,
      isUserDetailsLoading,
      logout,
      isLogoutPending,
      reloadUser,
      isUserPermitted,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }
  return context;
};

export { AuthProvider, useAuth };
